/**
 * Contract for the super-admin surface (decision D23).
 *
 * Every mutation here is recorded in `SystemAuditLog`. That is the point of the tier:
 * the actions are the ones nobody else may take, so they are the ones that most need
 * to be attributable afterwards.
 */
import { z } from 'zod';
import { departmentSchema, ticketPrioritySchema, ticketStatusSchema, userRoleSchema, } from '../enums.js';
/**
 * What a super-admin action did. Stored as a string column rather than a Postgres
 * enum so a new action never needs a migration — the audit log must never be the
 * reason a fix cannot ship.
 */
export const systemAuditActionSchema = z.enum([
    'DEACTIVATE_USER',
    'REACTIVATE_USER',
    'PROMOTE_USER',
    'DEMOTE_USER',
    'CREATE_ADMIN',
    // Setting somebody else's credential. The most impersonation-adjacent act the admin
    // tier offers — the route hands the new password back in its response, so whoever
    // performs it can sign in as the target — and it went unrecorded until 2026-08-26.
    //
    // The entry names the actor, the target and the address. It must never carry the
    // password itself, in `targetLabel`, `reason` or anywhere else: the response body is
    // the only place that value is allowed to exist.
    //
    // Written only when the actor is not the subject. Somebody changing their own password
    // is not performing an administrative act — the same line REVOKE_SESSION draws below —
    // and `User.passwordChangedAt` is that event's record.
    'ADMIN_SET_PASSWORD',
    // Superseded by ADMIN_SET_PASSWORD on 2026-08-27 (D10) and never written again.
    //
    // Kept in the vocabulary because it is still *readable*: rows written between
    // a03dc21 and that date carry it, `listAuditQuerySchema` parses this enum on the way
    // out, and dropping the value would make the audit view fail on the very history it
    // exists to show. An audit trail records what happened under the vocabulary of the
    // time; rewriting those rows to match a newer name would be falsifying it.
    'RESET_PASSWORD',
    'RETIRE_LOCATION',
    'REINSTATE_LOCATION',
    'CREATE_LOCATION',
    'EDIT_LOCATION',
    'SET_LOCATION_STATUS',
    // The two irreversible ones (D25). Recorded like anything else, with the caveat
    // that a deleted user's own entries lose their actor — which is the cost D25
    // accepts and the reason the label is stored on the entry rather than looked up.
    'DELETE_USER',
    'DELETE_LOCATION',
    // Job governance on the root tier (D27). A job is a Ticket — the queue technicians
    // work from is labelled "Jobs" — so these act on tickets and say so.
    'CREATE_JOB',
    'EDIT_JOB',
    'REASSIGN_JOB',
    'OVERRIDE_JOB_STATUS',
    'CANCEL_JOB',
    'DELETE_JOB',
    // Ending somebody else's signed-in device (D41). Self-revocation is not here: a person
    // tidying up their own old phones is not performing an administrative act, and the
    // session row's own revokedById/revokedReason already record it.
    'REVOKE_SESSION',
    // Rewriting the property's SLA windows (D8, honoured by Q-CONFIG-NOW). The numbers
    // decide when jobs escalate and what the dashboard calls a breach, so "who tightened
    // the windows before that breach spike" is a question the trail must answer.
    'SET_SLA_POLICY',
    // Moving the reporting baseline — the date the dashboard's figures count from.
    // The entry's `reason` carries old → new, because a reset with no trail is
    // indistinguishable from figures that were always good, and "who reset them,
    // from what" is asked precisely when the numbers look better than remembered.
    'SET_ANALYTICS_BASELINE',
]);
/**
 * Reason is mandatory on anything destructive, for the same reason retirement
 * carries one: an entry saying only *what* happened leaves the next person guessing
 * at *why*, which is when an audit trail stops being worth keeping.
 */
export const superAdminReasonSchema = z.string().trim().min(3).max(500);
/** Body of PATCH /api/super-admin/users/:id/deactivate. */
export const deactivateUserBodySchema = z.object({
    reason: superAdminReasonSchema,
});
/** Body of PATCH /api/super-admin/users/:id/role. */
export const changeUserRoleBodySchema = z.object({
    role: userRoleSchema,
    reason: superAdminReasonSchema,
});
/** Body of POST /api/super-admin/users — the only route that may mint an admin. */
export const createPrivilegedUserBodySchema = z.object({
    email: z.string().trim().toLowerCase().email().max(320),
    name: z.string().trim().min(1).max(120),
    role: userRoleSchema,
    department: departmentSchema,
});
/**
 * Lifecycle state of a place or room (decision D25).
 *
 * Richer than the `isActive` boolean it sits beside, which stays the single gate on
 * "may this be picked when reporting a fault". MAINTENANCE exists because a room being
 * refurbished is neither of the other two: it is temporarily unusable but coming back,
 * and retiring it would say the opposite.
 */
export const entityStatusSchema = z.enum(['ACTIVE', 'RETIRED', 'MAINTENANCE']);
/**
 * Body of PATCH /api/super-admin/locations/:id/status.
 *
 * RETIRED is deliberately not settable here — retiring runs orphan checks that this
 * route does not, so it keeps its own endpoint and this one moves between the two
 * usable states.
 */
/**
 * Body of PATCH /api/super-admin/locations/:id — correcting a place in situ.
 *
 * Exists because retirement and deletion are both the wrong tool for a typo. A
 * misspelled floor with sixteen rooms under it cannot be deleted — its children block
 * it, correctly — and must not be retired, because it is in daily use. Recreating it
 * would mean re-parenting every child onto a new id and losing the originals.
 *
 * Deliberately narrower than the admin `updateLocationBodySchema`:
 * - no `isActive`: retire and reinstate own that, and they run orphan checks this route
 *   does not.
 * - no `parentId`: moving a place across the tree can point a node at its own
 *   descendant, and nothing here walks the tree to prove otherwise. Fixing a name is a
 *   different job from restructuring the property.
 *
 * No reason is required, matching reinstate — this is corrective and reversible, and the
 * audit label carries the before and after, which explains itself.
 */
export const editLocationBodySchema = z.object({
    name: z.string().trim().min(1).max(120),
    floor: z.string().trim().max(40).nullable().optional(),
    qrCode: z.string().trim().min(1).max(80).nullable().optional(),
    department: departmentSchema.nullable().optional(),
});
export const setLocationStatusBodySchema = z.object({
    status: z.enum(['ACTIVE', 'MAINTENANCE']),
    reason: superAdminReasonSchema,
});
/**
 * Body of DELETE /api/super-admin/users/:id and /locations/:id (decision D25).
 *
 * Irreversible, so the reason is the only record of intent that survives the row. It
 * is required for exactly that: the deleted entity cannot be inspected afterwards.
 */
export const superAdminDeleteBodySchema = z.object({
    reason: superAdminReasonSchema,
    /**
     * Must equal the target's own name or email. A destructive action reachable by one
     * mis-tap on a phone in a plant room needs a deliberate second act, and typing the
     * name is the one confirmation that cannot be produced by a stray tap.
     */
    confirm: z.string().trim().min(1).max(320),
});
/**
 * Job governance on the root tier (decision D27).
 *
 * A "job" is a `Ticket`. The queue technicians work from is labelled Jobs, and a fault
 * reported by QR and a job raised by a manager are the same row — one work queue, one
 * audit trail, one SLA. These routes give the tier what it lacked: the ability to raise
 * work directly, correct it, and override a status the lifecycle would not otherwise
 * allow.
 */
export const createJobBodySchema = z.object({
    title: z.string().trim().min(3).max(160),
    description: z.string().trim().max(4000).optional(),
    priority: ticketPrioritySchema,
    department: departmentSchema,
    /** Where the work is. Optional: not every job is tied to a room. */
    locationId: z.string().min(1).nullable().optional(),
    /** Who is to do it. Optional — an unassigned job still routes by department. */
    assigneeId: z.string().min(1).nullable().optional(),
    /**
     * Overrides the deadline the SLA policy would derive from priority. Optional, because
     * the policy is the right answer nearly always; when it is not, this is why the field
     * exists rather than a second SLA table.
     */
    dueAt: z.coerce.date().optional(),
});
/**
 * Body of PATCH /api/super-admin/jobs/:id.
 *
 * Every field optional and `undefined` distinguished from `null` throughout: omitted
 * means "not sent", null means "clear it". Collapsing the two would blank an assignee
 * the form never showed — the same trap the D25 location edit had to avoid.
 *
 * Status is deliberately absent. Overriding it skips the lifecycle guards, so it has its
 * own route and its own audit action; folding it in here would let a description fix and
 * a forced state change arrive as one indistinguishable entry.
 */
export const editJobBodySchema = z
    .object({
    title: z.string().trim().min(3).max(160).optional(),
    description: z.string().trim().max(4000).nullable().optional(),
    priority: ticketPrioritySchema.optional(),
    department: departmentSchema.optional(),
    locationId: z.string().min(1).nullable().optional(),
    assigneeId: z.string().min(1).nullable().optional(),
    dueAt: z.coerce.date().optional(),
})
    // An empty PATCH would record an EDIT_JOB entry saying nothing changed, which is worse
    // than a 400: it puts noise in the trail the tier exists to keep readable.
    .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Send at least one field to change',
});
/**
 * Body of PATCH /api/super-admin/jobs/:id/status — the override.
 *
 * Reason is mandatory here and nowhere else in job editing. The ordinary transitions are
 * self-explaining because the lifecycle only permits sensible ones; this route exists
 * precisely to make a move the lifecycle refuses, so the trail needs to say why.
 */
export const overrideJobStatusBodySchema = z.object({
    status: ticketStatusSchema,
    reason: superAdminReasonSchema,
});
/** Query for GET /api/super-admin/audit. */
export const listAuditQuerySchema = z.object({
    action: systemAuditActionSchema.optional(),
    actorId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=super-admin.js.map